import React from 'react';
import { Phone, Mail, MapPin, Instagram, Clock } from 'lucide-react';
import { useData } from '../../context/DataContext';

const ContactPage = () => {
  const { siteSettings, isLoading } = useData();

  const whatsappUrl = `https://wa.me/${siteSettings?.whatsappNumber || '905546500984'}?text=${encodeURIComponent('Merhaba Taşdemir İnşaat, bilgi almak istiyorum.')}`;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-anthracite">
      {/* Hero */}
      <section className="relative py-24 bg-anthracite-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Bize Ulaşın
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-4">
              İletişim
            </h1>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Projelerimiz hakkında detaylı bilgi almak veya iş birliği fırsatlarını değerlendirmek için bizimle iletişime geçin.
            </p>
            <div className="w-24 h-1 bg-gold mx-auto mt-6" />
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-8">İletişim Bilgileri</h2>
              
              <div className="space-y-6">
                {/* Phone 1 */}
                <a
                  href={`tel:${siteSettings?.phone1?.replace(/\s/g, '')}`}
                  className="flex items-start space-x-4 p-6 bg-anthracite-light rounded-lg border border-gold/10 hover:border-gold/30 transition-all duration-300 group"
                >
                  <div className="p-3 bg-gold/10 rounded-lg group-hover:bg-gold/20 transition-colors duration-300">
                    <Phone className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Telefon 1</h3>
                    <p className="text-gray-400">{siteSettings?.phone1}</p>
                  </div>
                </a>

                {/* Phone 2 */}
                <a
                  href={`tel:${siteSettings?.phone2?.replace(/\s/g, '')}`}
                  className="flex items-start space-x-4 p-6 bg-anthracite-light rounded-lg border border-gold/10 hover:border-gold/30 transition-all duration-300 group"
                >
                  <div className="p-3 bg-gold/10 rounded-lg group-hover:bg-gold/20 transition-colors duration-300">
                    <Phone className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Telefon 2</h3>
                    <p className="text-gray-400">{siteSettings?.phone2}</p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href={`mailto:${siteSettings?.email}`}
                  className="flex items-start space-x-4 p-6 bg-anthracite-light rounded-lg border border-gold/10 hover:border-gold/30 transition-all duration-300 group"
                >
                  <div className="p-3 bg-gold/10 rounded-lg group-hover:bg-gold/20 transition-colors duration-300">
                    <Mail className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">E-posta</h3>
                    <p className="text-gray-400">{siteSettings?.email}</p>
                  </div>
                </a>

                {/* Address */}
                <div className="flex items-start space-x-4 p-6 bg-anthracite-light rounded-lg border border-gold/10">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <MapPin className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Adres</h3>
                    <p className="text-gray-400">{siteSettings?.address}</p>
                  </div>
                </div>

                {/* Instagram */}
                <a
                  href={siteSettings?.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-4 p-6 bg-anthracite-light rounded-lg border border-gold/10 hover:border-gold/30 transition-all duration-300 group"
                >
                  <div className="p-3 bg-gold/10 rounded-lg group-hover:bg-gold/20 transition-colors duration-300">
                    <Instagram className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Instagram</h3>
                    <p className="text-gray-400">@tasdemir_insaat.a.s</p>
                  </div>
                </a>

                {/* Working Hours */}
                <div className="flex items-start space-x-4 p-6 bg-anthracite-light rounded-lg border border-gold/10">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <Clock className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Çalışma Saatleri</h3>
                    <p className="text-gray-400">Pazartesi - Cumartesi: 09:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map & WhatsApp CTA */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-8">Konum</h2>
              
              {/* Map */}
              <div className="aspect-[4/3] rounded-lg overflow-hidden border border-gold/10 mb-8">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3124.5!2d27.2!3d38.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDI0JzAwLjAiTiAyN8KwMTInMDAuMCJF!5e0!3m2!1str!2str!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Taşdemir İnşaat Konum"
                />
              </div>

              {/* WhatsApp CTA */}
              <div className="p-8 bg-anthracite-light rounded-lg border border-gold/10">
                <h3 className="text-xl font-bold text-white mb-4">
                  Hızlı İletişim
                </h3>
                <p className="text-gray-400 mb-6">
                  En hızlı şekilde bilgi almak için WhatsApp üzerinden bize ulaşabilirsiniz.
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-3 w-full px-8 py-4 bg-gold text-anthracite font-bold text-lg rounded hover:bg-gold-light transition-all duration-300 hover:shadow-lg hover:shadow-gold/20"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>WhatsApp'ıtan Yaz</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;